// encounter-view.js — CLIENT-SIDE shaping, raw FHIR -> the buildEncounterView
// DTO (server/opd/view-adapter.mjs:1798-2157) that ConsultCompact.jsx +
// consult-kit.jsx + Writer.jsx read through OPDBridge.pick('encounter.…').
//
// This is the pure shaper (no MedplumClient) — medplum-encounter.js does the
// one graph fetch and calls buildEncounterView(graph) below. Kept pure so a
// Node test can feed it a synthetic bundle.
//
// WHAT THE REDESIGN CHANGES vs. the server DTO:
//   • The disk-only `_healther` snapshot bags (patientSnapshot / appointmentSnapshot)
//     do NOT exist on a plain FHIR fetch. Under online-primary the REAL Patient /
//     Encounter / Practitioner resources are the source, so every field the server
//     read off `ps`/`ap` is re-derived here from those resources instead. Where a
//     field has no FHIR home yet (e.g. `paid`, `language`), we emit the screen's
//     tolerated default — never invented clinical data.
//   • The disk-only `_payload` (structured intake section) and `_draft` (consult
//     draft) bags now live in FHIR extensions, per the seed convention
//     (scripts/medplum/seed-demo-queue.mjs:21-22 + REDESIGN §12):
//       Composition.section[].extension[url=OPD_SECTION_PAYLOAD].valueString = JSON  → _payload
//       Composition.extension[url=OPD_DRAFT].valueString = JSON (on the consult-draft) → _draft
//     We read those extensions and JSON.parse them back into the same p/_draft shapes
//     the server's shapeIntakeSections / shapeConsultDraft consumed.
//   • Lock-1 chart-read-gate fields are DROPPED, not ported: the redesign invariant
//     is "authorized reads only, no app-side re-check" (AGENTS.md §2). This graph
//     was RLS-scoped by the doctor's own token, so it IS authorized — there is
//     nothing left to gate. (accessDenied is therefore always null here.)
//   • Best-effort SERVER-SIDE enrichments that need a second longitudinal query or
//     an LLM (priorContext, insights/insightsRanked/insightsBar, markers,
//     vitalSeries, episode, conflicts, aiQuality verifier/clinicalSense,
//     med-duration/first-noted backfill) are emitted as the SAME empty-but-present
//     shapes the server falls back to when those engines are unavailable. The
//     screens already tolerate these (that's the server's own catch-branch shape),
//     so the consult renders — the online-primary layer can grow them into direct
//     FHIR/Bot queries later without changing any screen. Each is commented at its
//     emit site below.

const OPD_STATE = 'https://healther.io/opd/state';
const OPD_TOKEN = 'https://healther.io/opd/token';
const MRN_SYS = 'https://healther.io/opd/mrn';
// Extension homes for the formerly disk-only bags (seed-demo-queue.mjs:21-22).
const OPD_SECTION_PAYLOAD = 'https://healther.io/opd/section-payload';
const OPD_DRAFT = 'https://healther.io/opd/draft';
const SIGN_SYS = 'https://healther.io/opd/sign';
// Browser-direct Media bags (medplum-consult-extras.js writer). A Media created
// online by the doctor carries its session/transcript payloads as `valueString`
// JSON extensions — NOT the disk-shaped `_healther`/`_transcript` fields the old
// server mirror produced. The read shapers below decode EITHER, so a browser-
// written consult transcript round-trips (this is the "recording not populated"
// fix — before it, mediaField/mediaTranscript only saw the disk shape and the
// saved consult-transcript Media was silently dropped on reload).
const EXT_OPD_HEALTHER = 'http://healther.io/fhir/StructureDefinition/opd-healther';
const EXT_OPD_TRANSCRIPT = 'http://healther.io/fhir/StructureDefinition/opd-consult-transcript';
// The consult-draft Composition FHIR contract (redesign): a preliminary
// Composition whose type coding.code is 'consult-draft', carrying the draft
// JSON in a single OPD_DRAFT extension. The write agent (saveDraft PATCH /
// sign POST) round-trips against this same resource.
const CONSULT_DRAFT_CODE = 'consult-draft';
const VISIT_SUMMARY_CODE = 'visit-summary';

// Internal encounter state → the view state string the screens render. Mirrors
// the server's STATE_TO_VIEW but keyed off the OPD_STATE extension (already the
// view-level string per shapeQueueRow) with a FHIR-status fallback.
const STATUS_TO_STATE = { planned: 'DRAFT', arrived: 'READY', triaged: 'READY', 'in-progress': 'IN_CONSULT', finished: 'COMPLETED', cancelled: 'DRAFT' };

import { formatClinicTime } from '../clinic-time.js';

const ext = (res, url) => ((res && res.extension) || []).find((e) => e.url === url);
const nameOf = (p) => { if (!p || !p.name || !p.name[0]) return 'Patient'; const n = p.name[0]; return n.text || [(n.given || []).join(' '), n.family].filter(Boolean).join(' ') || 'Patient'; };
const ageOf = (bd) => { if (!bd) return null; const y = new Date().getUTCFullYear() - Number(String(bd).slice(0, 4)); return isNaN(y) ? null : y; };
const initials = (name) => String(name || '').trim().split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
const strip = (s) => String(s || '').replace(/<[^>]*>/g, '').trim();
const fmtTime = (iso) => formatClinicTime(iso); // clinic wall-clock, not viewer device tz

// The state string the FHIR extension already stores IS the view state (same as
// shapeQueueRow) — no internal→view remap needed. Fall back to FHIR status.
function stateOf(enc) {
  const e = ext(enc, OPD_STATE);
  return (e && e.valueString) || STATUS_TO_STATE[enc && enc.status] || 'DRAFT';
}

// section[].extension[OPD_SECTION_PAYLOAD] → the structured `_payload` bag the
// server's shapeIntakeSections switch reads. Absent on sections that only carry
// a text.div (the current seed writes chief_complaint that way) → {}.
function sectionPayload(section) {
  const e = ext(section, OPD_SECTION_PAYLOAD);
  if (e && e.valueString) { try { return JSON.parse(e.valueString) || {}; } catch { return {}; } }
  return {};
}

// ── chief-complaint verbatim/clinical split (view-adapter.mjs:182-234) ──────
// Guarantees the bold heading (.doctor/.text) is CLINICAL ONLY — spoken
// verbatim (Devanagari / Hindi colloquial) is stripped into .patient/.verbatim
// and never lost. Ported verbatim so the redesign keeps the same guarantee.
const CC_DEVANAGARI_RE = /[ऀ-ॿ]/;
const CC_DURATION_RE = /\b\d+\s*(?:day|days|week|weeks|month|months|year|years|hr|hrs|hour|hours)\b/i;
const CC_CLINICAL_ANCHOR_RE = /\b(pain|fever|cough|cold|swelling|nausea|vomit|diarrhea|diarrhoea|headache|dizziness|breathless|dyspnea|chest|abdominal|rash|bleeding|discharge|weakness|fatigue|palpitation|burning|itching|numbness)\b/i;
const CC_HINDI_RE = /\b(hai|hain|raha|rahi|rahe|mujhe|mera|meri|mere|ko|se|ka|ki|ke|bahut|thoda|dard|bukhar|khansi)\b/i;

function ccSplitSentences(text) {
  return String(text || '').split(/(?<=[.!?।])\s+|\n+/).map((s) => s.trim()).filter(Boolean);
}
function ccIsVerbatimSentence(s) {
  const t = String(s || '').trim();
  if (!t) return false;
  if (CC_DEVANAGARI_RE.test(t)) return true;
  if (CC_CLINICAL_ANCHOR_RE.test(t) || CC_DURATION_RE.test(t)) return false;
  return CC_HINDI_RE.test(t);
}
function splitChiefComplaintHeading({ doctor, patient, verbatim }) {
  const headingRaw = String(doctor || '').trim();
  const patientRaw = String(patient || '').trim();
  const verbatimRaw = String(verbatim || '').trim();
  if (!headingRaw) return { doctor: '', patient: patientRaw || verbatimRaw, verbatim: verbatimRaw };
  const sentences = ccSplitSentences(headingRaw);
  const clinical = []; const spoken = [];
  for (const s of sentences) (ccIsVerbatimSentence(s) ? spoken : clinical).push(s);
  const cleanedDoctor = clinical.join(' ').trim() || headingRaw;
  const patientDistinct = patientRaw && patientRaw !== headingRaw ? patientRaw : '';
  const patientLine = patientDistinct || spoken.join(' ').trim() || verbatimRaw || patientRaw;
  return { doctor: cleanedDoctor, patient: patientLine, verbatim: verbatimRaw || patientLine };
}

// QuestionnaireResponse step text (view-adapter.mjs:236-239).
function qrStepText(qr, n) {
  const step = qr && qr.item && qr.item.find((it) => it.linkId === `step${n}`);
  return (step && step.item && step.item[0] && step.item[0].answer && step.item[0].answer[0] && step.item[0].answer[0].valueString) || '';
}

// ── crossed-stub scrubbers (view-adapter.mjs:606-648) ───────────────────────
// The offline stub extraction copies the same step text into allergies +
// past_conditions + current_medications + family_history; these strip the
// meds line out of allergy/family free-text so a drug never surfaces there.
function medNameKeysFromIntake(intake) {
  const keys = new Set();
  for (const m of (intake && intake.current_medications && intake.current_medications.items) || []) {
    const raw = String((m && m.name) || '').trim();
    if (!raw) continue;
    const k = raw.toLowerCase().replace(/\b\d+(?:\.\d+)?\s*(?:mg|mcg|ml|gm?|iu|units?|%|od|bd|tds|hs)\b/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
    for (const tok of k.split(' ')) if (tok.length >= 4) keys.add(tok);
  }
  return keys;
}
function textMentionsMed(text, medKeys) {
  if (!text || !medKeys || !medKeys.size) return false;
  const t = String(text).toLowerCase();
  for (const k of medKeys) if (t.includes(k)) return true;
  return false;
}
const ABSENT_ALLERGY_RE = /\bno known\b|\bnone known\b|\bno (?:drug )?allerg/i;
function stripCrossedSentences(text, medKeys) {
  if (!text) return '';
  return String(text).split(/(?<=[.!?।])\s+|\n+/).map((s) => s.trim())
    .filter((s) => s && !ABSENT_ALLERGY_RE.test(s) && !textMentionsMed(s, medKeys)).join(' ').trim();
}
function stripRelationFromCondition(relation, condition) {
  let c = String(condition || '').trim();
  const rel = String(relation || '').trim();
  if (rel && c.toLowerCase().startsWith(rel.toLowerCase())) c = c.slice(rel.length).replace(/^[\s:.,;–—-]+/, '').trim();
  return c;
}

// ── intake tree (view-adapter.mjs:241-527) ──────────────────────────────────
// Reproduces shapeIntakeSections: reads the freshest intake-summary Composition's
// sections (via the OPD_SECTION_PAYLOAD extension for structured data, else the
// text.div free-text), else falls back to the raw QR step text. Every key is
// present even when empty (drives the "dashed card" missing UI).
// Encounter.reasonCode[0] → a chief_complaint fallback. A walk-in created via
// the doctor quick-add / call-in (medplum-callin.js) or a kiosk-routed walk-in
// has NO intake Composition yet — the presenting complaint lives ONLY on
// Encounter.reasonCode[].text (or .coding[0].display). Without this the
// complaint the doctor/kiosk captured is write-only: stored but never surfaced
// on the consult. Returns a chief_complaint DTO or null.
function chiefComplaintFromEncounter(enc) {
  const rc = enc && Array.isArray(enc.reasonCode) && enc.reasonCode[0];
  if (!rc) return null;
  const text = String(rc.text
    || (rc.coding && rc.coding[0] && (rc.coding[0].display || rc.coding[0].code)) || '').trim();
  if (!text) return null;
  return { text, doctor: text, patient: text, secondary: [], verbatim: text };
}

export function shapeIntakeSections(composition, qr, encounter) {
  const step1 = qrStepText(qr, 1);
  const step2 = qrStepText(qr, 2);
  const step3 = qrStepText(qr, 3);
  const step4 = qrStepText(qr, 4);
  const step5 = qrStepText(qr, 5);
  const healthBackgroundText = [step3, step4].filter(Boolean).join(' ');
  const verbatim = { step1, step2, step3, step4, step5 };
  const empty = {
    personal_information: { text: step1 || '', structured: null, verbatim: step1 || '' },
    chief_complaint: null,
    history_present_illness: { bullets: [], verbatim: step2 || '' },
    allergies: { items: [], status: 'unknown', verbatim: step4 || '' },
    past_conditions: { items: [], verbatim: step3 || '' },
    current_medications: { items: [], verbatim: step4 || '' },
    family_history: { items: [], verbatim: step3 || '' },
    lifestyle: { bullets: [], verbatim: step5 || '' },
    additional_information: { items: [] },
    health_background: { text: healthBackgroundText },
    verbatim,
    missing: [],
  };

  // Persisted live AI facts on the QR (written by /intake/live-extract). Under
  // the redesign these ride in a QR item extension (OPD_SECTION_PAYLOAD on the
  // '.facts' sub-item) rather than the disk-only `_facts` array. We read both so
  // an already-persisted facts array still works.
  const liveFacts = [];
  for (const it of (qr && qr.item) || []) {
    for (const sub of (it.item || [])) {
      if (!(sub && sub.linkId && sub.linkId.endsWith('.facts'))) continue;
      let facts = Array.isArray(sub._facts) ? sub._facts : null;
      if (!facts) { const e = ext(sub, OPD_SECTION_PAYLOAD); if (e && e.valueString) { try { facts = JSON.parse(e.valueString); } catch { facts = null; } } }
      for (const f of (facts || [])) if (f && f.label && f.value) liveFacts.push(f);
    }
  }
  const liveFactsAsPersonal = () => {
    const map = { name: ['name', 'नाम'], age: ['age', 'उम्र'], sex: ['sex', 'gender', 'लिंग'], phone: ['phone', 'फ़ोन', 'फोन', 'mobile'], locality: ['locality', 'address', 'city', 'पता', 'शहर'] };
    const s = {};
    for (const [key, hints] of Object.entries(map)) { const f = liveFacts.find((x) => hints.some((h) => String(x.label || '').toLowerCase().includes(h))); if (f) s[key] = f.value; }
    return Object.keys(s).length ? s : null;
  };
  const liveFactsAsLifestyle = () => {
    const bullets = [];
    const map = { smoking: ['smok', 'धूम्रपान'], alcohol: ['alcohol', 'drink', 'दारू', 'शराब'], exercise: ['exercise', 'walk', 'व्यायाम'], sleep: ['sleep', 'नींद'], work: ['work', 'job', 'काम'], diet: ['diet', 'खाना', 'food'] };
    for (const [key, hints] of Object.entries(map)) { const f = liveFacts.find((x) => hints.some((h) => String(x.label || '').toLowerCase().includes(h))); if (f) bullets.push(`${key.charAt(0).toUpperCase() + key.slice(1)}: ${f.value}`); }
    return bullets;
  };
  const liveFactsAsAdditional = () => {
    const known = ['name', 'age', 'sex', 'gender', 'phone', 'locality', 'address', 'smok', 'alcohol', 'drink', 'exercise', 'walk', 'sleep', 'work', 'diet', 'chief complaint', 'onset', 'severity', 'location', 'allergy', 'allergies', 'current medicine', 'medication', 'family'];
    return liveFacts.filter((f) => !known.some((h) => String(f.label || '').toLowerCase().includes(h))).map((f) => ({ label: f.label, value: f.value }));
  };

  if (!(composition && composition.section)) {
    // No intake Composition yet — full missing list + live-fact fallback, exactly
    // like the server's no-composition branch (view-adapter.mjs:307-342).
    const personalStruct = liveFactsAsPersonal();
    const lifestyleBullets = liveFactsAsLifestyle();
    const fallback = {
      ...empty,
      personal_information: {
        text: personalStruct ? [personalStruct.name, personalStruct.age, personalStruct.sex, personalStruct.phone, personalStruct.locality].filter(Boolean).join(' · ') : empty.personal_information.text,
        structured: personalStruct, verbatim: step1 || '',
      },
      // Prefer the intake step text; else fall back to Encounter.reasonCode (a
      // quick-add / kiosk walk-in has no intake, only reasonCode).
      chief_complaint: step1 ? { text: step1, verbatim: step1 } : chiefComplaintFromEncounter(encounter),
      history_present_illness: { bullets: step2 ? [step2] : [], verbatim: step2 || '' },
      allergies: step4 ? { items: [], status: 'unknown', text: step4, verbatim: step4 } : empty.allergies,
      past_conditions: step3 ? { items: [{ condition: step3, since: null, status: null }], verbatim: step3 } : empty.past_conditions,
      family_history: step3 ? { items: [], verbatim: step3 } : empty.family_history,
      lifestyle: { bullets: lifestyleBullets.length ? lifestyleBullets : (step5 ? [step5] : []), verbatim: step5 || '' },
      additional_information: { items: liveFactsAsAdditional() },
    };
    const missing = [];
    if (!fallback.personal_information.text) missing.push('Personal information');
    if (!fallback.chief_complaint) missing.push('Chief complaint');
    if (!fallback.history_present_illness.bullets.length) missing.push('History of present illness');
    if (!step4) missing.push('Allergies');
    if (!step3) missing.push('Past conditions');
    if (!step4) missing.push('Current medications');
    if (!step3) missing.push('Family history');
    if (!fallback.lifestyle.bullets.length) missing.push('Lifestyle');
    return { ...fallback, missing };
  }

  const out = { ...empty };
  for (const s of composition.section) {
    const code = s.code && s.code.coding && s.code.coding[0] && s.code.coding[0].code;
    // `p` is the structured bag: the OPD_SECTION_PAYLOAD extension JSON, else a
    // one-field {text} synthesised from the section's rendered text.div so a
    // seed/import that only wrote the div (the current chief_complaint seed)
    // still populates.
    const p = sectionPayload(s);
    const divText = s.text && s.text.div ? strip(s.text.div) : '';
    if (!Object.keys(p).length && divText) p.text = divText;
    switch (code) {
      case 'personal_information': {
        const structured = (p.name || p.age || p.sex || p.occupation || p.phone || p.locality)
          ? { name: p.name || null, age: p.age || null, sex: p.sex || null, occupation: p.occupation || null, phone: p.phone || null, locality: p.locality || null } : null;
        const text = (structured ? [structured.occupation, structured.locality].filter(Boolean).join(' · ') : '') || p.text || '';
        out.personal_information = { text, structured, verbatim: step1 || '' };
        break;
      }
      case 'chief_complaint': {
        const split = splitChiefComplaintHeading({ doctor: p.doctor || p.text || '', patient: p.patient || '', verbatim: step1 || p.verbatim || '' });
        out.chief_complaint = {
          text: split.doctor, doctor: split.doctor,
          patient: split.patient || step1 || p.verbatim || '',
          secondary: Array.isArray(p.secondary) ? p.secondary.filter(Boolean) : [],
          verbatim: step1 || p.verbatim || split.verbatim || '',
        };
        break;
      }
      case 'history_present_illness': {
        const bullets = Array.isArray(p.items) ? p.items.map((i) => (typeof i === 'string' ? i : (i.text || i.point || JSON.stringify(i)))) : (p.text ? [p.text] : []);
        out.history_present_illness = { bullets, verbatim: step2 || '' };
        break;
      }
      case 'allergies': {
        const items = Array.isArray(p.items) ? p.items.map((i) => ({ name: i.name || i.allergen || (typeof i === 'string' ? i : 'Allergy') })) : [];
        let status = 'unknown';
        if (p.status === 'present' || items.length) status = 'present';
        else if (p.status === 'absent' || p.text === 'None known' || p.text === 'No known allergies') status = 'absent';
        out.allergies = { items, status, text: p.text || '' };
        break;
      }
      case 'past_conditions': {
        const items = Array.isArray(p.items)
          ? p.items.map((i) => ({ condition: i.condition || i.name || (typeof i === 'string' ? i : 'Condition'), since: i.since || null, status: i.status || null }))
          : (p.text ? [{ condition: p.text, since: null, status: null }] : []);
        out.past_conditions = { items, verbatim: step3 || '' };
        break;
      }
      case 'current_medications': {
        const items = Array.isArray(p.items)
          ? p.items.map((i) => ({ name: i.name || (typeof i === 'string' ? i : 'Medication'), dose: i.dose || i.strength || '', frequency: i.frequency || i.freq || '', duration: i.duration || i.since || '', route: i.route || '', timing: i.timing || i.when || i.lastTaken || '' }))
          : (p.text ? [{ name: p.text, dose: '', frequency: '', duration: '', route: '', timing: '' }] : []);
        out.current_medications = { items, verbatim: step4 || '' };
        break;
      }
      case 'family_history': {
        const items = Array.isArray(p.items)
          ? p.items.map((i) => ({ relation: i.relation || 'family member', condition: i.condition || (typeof i === 'string' ? i : 'condition') }))
          : (p.text ? [{ relation: 'Family', condition: p.text }] : []);
        out.family_history = { items, verbatim: step3 || '' };
        break;
      }
      case 'lifestyle': {
        const bullets = [];
        if (p.smoking) bullets.push(`Smoking: ${p.smoking}`);
        if (p.alcohol) bullets.push(`Alcohol: ${p.alcohol}`);
        if (p.exercise) bullets.push(`Exercise: ${p.exercise}`);
        if (p.sleep) bullets.push(`Sleep: ${p.sleep}`);
        if (p.work) bullets.push(`Work: ${p.work}`);
        if (p.diet) bullets.push(`Diet: ${p.diet}`);
        if (!bullets.length && p.text) bullets.push(p.text);
        out.lifestyle = { bullets, verbatim: step5 || '' };
        break;
      }
      case 'additional_information': {
        const items = Array.isArray(p.items) ? p.items.map((i) => ({ label: i.label || i.title || '', value: i.value || i.text || (typeof i === 'string' ? i : '') })).filter((i) => i.label && i.value) : [];
        out.additional_information = { items };
        break;
      }
      default: break;
    }
  }
  // Backfill from live facts (view-adapter.mjs:461-471).
  if (!out.additional_information.items.length) out.additional_information = { items: liveFactsAsAdditional() };
  if (!out.personal_information.structured) out.personal_information.structured = liveFactsAsPersonal();
  if (!out.lifestyle.bullets.length) { const lb = liveFactsAsLifestyle(); if (lb.length) out.lifestyle = { bullets: lb, verbatim: step5 || '' }; }

  // Crossed-stub scrub (view-adapter.mjs:477-513) — keep meds out of allergies /
  // family / past, split a comma-joined meds item into distinct rows.
  {
    const medKeys = medNameKeysFromIntake(out);
    if (medKeys.size) {
      if (Array.isArray(out.allergies.items)) out.allergies.items = out.allergies.items.filter((x) => !textMentionsMed(x && x.name, medKeys));
      if (textMentionsMed(out.allergies.text, medKeys)) out.allergies.text = '';
      if (!(out.allergies.items || []).length && out.allergies.status === 'present') out.allergies.status = 'unknown';
      if (Array.isArray(out.family_history.items)) out.family_history.items = out.family_history.items.map((f) => ({ ...f, condition: stripCrossedSentences(f && f.condition, medKeys) })).filter((f) => f.condition);
      if (Array.isArray(out.past_conditions.items)) out.past_conditions.items = out.past_conditions.items.map((c) => ({ ...c, condition: stripCrossedSentences(c && c.condition, medKeys) })).filter((c) => c.condition);
    }
    if (Array.isArray(out.current_medications.items)) {
      const split = [];
      for (const it of out.current_medications.items) {
        const name = String((it && it.name) || '').trim();
        const parts = name.split(/\s*,\s*/).map((s) => s.replace(/\.$/, '').trim()).filter(Boolean);
        if (parts.length > 1) parts.forEach((part) => split.push({ ...it, name: part }));
        else if (name) split.push(it);
      }
      out.current_medications.items = split;
    }
  }
  // Chief-complaint fallback: an intake Composition may exist (with other
  // sections) yet carry NO chief_complaint section — a walk-in's complaint then
  // lives only on Encounter.reasonCode. Fall back to it before marking missing.
  if (!out.chief_complaint) out.chief_complaint = chiefComplaintFromEncounter(encounter);

  // Missing sections (view-adapter.mjs:516-525).
  const missing = [];
  if (!out.personal_information.text) missing.push('Personal information');
  if (!out.chief_complaint) missing.push('Chief complaint');
  if (!out.history_present_illness.bullets.length) missing.push('History of present illness');
  if (out.allergies.status === 'unknown' && !step4) missing.push('Allergies');
  if (!out.past_conditions.items.length && !step3) missing.push('Past conditions');
  if (!out.current_medications.items.length && !step4) missing.push('Current medications');
  if (!out.family_history.items.length && !step3) missing.push('Family history');
  if (!out.lifestyle.bullets.length) missing.push('Lifestyle');
  out.missing = missing;
  return out;
}

// shapeIntakeVerbatim (view-adapter.mjs:548-554).
export function shapeIntakeVerbatim(qr) {
  if (!(qr && qr.item)) return [];
  return qr.item.map((step) => ({ stepId: step.linkId, label: step.text || step.linkId, text: (step.item && step.item[0] && step.item[0].answer && step.item[0].answer[0] && step.item[0].answer[0].valueString) || '' })).filter((x) => x.text);
}

// shapeIntakeLiveFacts (view-adapter.mjs:532-545). Reads _facts array, else the
// OPD_SECTION_PAYLOAD extension on each '.facts' sub-item.
export function shapeIntakeLiveFacts(qr) {
  const out = [];
  for (const it of (qr && qr.item) || []) {
    for (const sub of (it.item || [])) {
      if (!(sub && sub.linkId && sub.linkId.endsWith('.facts'))) continue;
      let facts = Array.isArray(sub._facts) ? sub._facts : null;
      if (!facts) { const e = ext(sub, OPD_SECTION_PAYLOAD); if (e && e.valueString) { try { facts = JSON.parse(e.valueString); } catch { facts = null; } } }
      for (const f of (facts || [])) { if (!(f && f.label && f.value)) continue; out.push({ ...f, step: f.step || Number(String(it.linkId || '').replace(/[^0-9]/g, '')) || null }); }
    }
  }
  return out;
}

// ── consult draft (view-adapter.mjs:557-577) ────────────────────────────────
// Reads the consult-draft Composition's OPD_DRAFT extension JSON into the draft
// shape ConsultCompact reads (findings/diagnosis/icd10/medications/…). The
// medplum-encounter loader exposes the draft Composition's id + versionId
// separately so the write agent can PATCH/sign with If-Match.
export function draftJsonFromComposition(rxComp) {
  if (!rxComp) return {};
  if (rxComp._draft) return rxComp._draft; // disk-shadow compatibility
  const e = ext(rxComp, OPD_DRAFT);
  if (e && e.valueString) { try { return JSON.parse(e.valueString) || {}; } catch { return {}; } }
  return {};
}
export function shapeConsultDraft(rxComp) {
  const draft = draftJsonFromComposition(rxComp);
  return {
    findings: draft.findings || draft.findings_diagnosis || '',
    diagnosis: draft.diagnosis || draft.provisional_diagnosis || '',
    icd10: draft.icd10 || null,
    medications: (draft.medications || []).map((m) => ({
      name: m.name || '', strength: m.strength || m.dose || '', route: m.route || 'PO',
      frequency: m.frequency || m.freq || '', duration: m.duration || '',
      instructions: m.instructions || '', indication: m.indication || '',
      confidence: typeof m.confidence === 'number' ? m.confidence : 1,
    })),
    investigations: (draft.investigations || []).map((x) => typeof x === 'string' ? { name: x, region: '', why: '' } : { name: x.name || '', region: x.region || '', why: x.why || x.reason || '' }),
    followup: draft.followup || '',
    instructions: draft.patient_instructions || draft.instructions || '',
  };
}

// Writer.jsx (the prescription pad) reads the SAME draft through
// encounter.consult.draft.* but its MedCard requires the mockup's STRUCTURED
// shape — freq as an [M,A,E,N] array (FreqDial calls value.join), duration as
// {n, unit}, per-row id — while ConsultCompact reads encounter.draft with the
// STRING shape (frequency '1-0-1', duration '5 days'). The two mockups
// genuinely disagree, and the mockups are the contract (AGENTS.md §7): each
// pick-path gets its own screen's shape. (The server DTO ships the string
// shape on both paths — Writer.jsx only ever rendered its built-in mockup
// fallback before; feeding it real data exposed this.)
function freqArrayFrom(frequency) {
  const parts = String(frequency || '').split('-').map((x) => Number(x));
  const arr = [0, 0, 0, 0];
  for (let i = 0; i < 4 && i < parts.length; i++) arr[i] = isNaN(parts[i]) ? 0 : parts[i];
  return arr;
}
function durationObjFrom(duration) {
  const m = String(duration || '').match(/(\d+)\s*(day|week|month)/i);
  return m ? { n: Number(m[1]), unit: m[2].toLowerCase() + 's' } : { n: '', unit: 'days' };
}
function toWriterDraft(draft) {
  return {
    ...draft,
    medications: (draft.medications || []).map((m, i) => ({
      id: 'm' + (i + 1),
      name: m.name || '',
      strength: m.strength || '',
      freq: freqArrayFrom(m.frequency),
      route: m.route || 'PO',
      duration: durationObjFrom(m.duration),
      instructions: m.instructions || '',
      indication: m.indication || '',
    })),
    investigations: (draft.investigations || []).map((x, i) => ({ id: 'i' + (i + 1), name: x.name || '', note: x.why || x.region || '' })),
  };
}

// shapeConsultTranscript (view-adapter.mjs:579-598). Media of sessionType
// 'opd-consult' + their transcript. Under the redesign the disk-only
// _healther/_transcript bags ride in Media extensions; we read either.
// Decode a Media's `valueString` JSON extension bag (browser-direct write shape).
// PURE, tolerant of a malformed payload.
function mediaBag(m, url) {
  const e = ext(m, url);
  if (e && e.valueString) { try { return JSON.parse(e.valueString) || null; } catch { return null; } }
  return null;
}
function mediaField(m, key) {
  // Disk-shaped `_healther` field first (server mirror), then the browser-direct
  // opd-healther extension bag.
  if (m && m._healther && m._healther[key] != null) return m._healther[key];
  const bag = mediaBag(m, EXT_OPD_HEALTHER);
  if (bag && bag[key] != null) return bag[key];
  return null;
}
function mediaTranscript(m) {
  // Disk-shaped `_transcript` field first, then the browser-direct opd-consult-
  // transcript extension bag (carries the byte-exact per-turn `turns` array).
  if (m && m._transcript && m._transcript.transcript) return { transcript: m._transcript.transcript, durationMs: m._transcript.durationMs || 0, turns: m._transcript.turns || null };
  const bag = mediaBag(m, EXT_OPD_TRANSCRIPT);
  if (bag && (bag.transcript || (Array.isArray(bag.turns) && bag.turns.length))) {
    return { transcript: bag.transcript || '', durationMs: bag.durationMs || 0, turns: Array.isArray(bag.turns) ? bag.turns : null };
  }
  return { transcript: '', durationMs: 0, turns: null };
}
// Speaker string → transcript role. cleanTurns() drops the per-turn `role`, so it
// is re-derived here exactly as ConsultLive.appendFinal and the Words-tab render
// do (a "Dr."/"Doctor" speaker is the clinician, everyone else the patient).
function turnRole(speaker) {
  return /^dr\.?\b|doctor/i.test(String(speaker || '')) ? 'doctor' : 'patient';
}
function shortStamp(ms) {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  const hh = Math.floor(s / 3600); const mm = Math.floor((s % 3600) / 60); const ss = s % 60;
  return `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}
// base64 → utf-8 text, browser (atob) or Node (Buffer) — the transcript
// DocumentReference carries the verbatim as attachment.data.
function b64ToText(b64) {
  try {
    if (typeof Buffer !== 'undefined') return Buffer.from(b64, 'base64').toString('utf-8');
    return decodeURIComponent(Array.prototype.map.call(atob(b64), (c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
  } catch { return ''; }
}
export function shapeConsultTranscript(mediaList, docRefs) {
  const bubbles = [];
  let elapsed = 0;
  for (const m of (Array.isArray(mediaList) ? mediaList : [])) {
    if (mediaField(m, 'sessionType') !== 'opd-consult') continue;
    const t = mediaTranscript(m);
    // Byte-exact per-turn payload (browser-direct write): expand each captured
    // turn into its own chat bubble so a reloaded consult transcript reads like
    // the live Words tab (doctor/patient turns), not one collapsed blob. Each
    // bubble carries BOTH the shaped field names and the live-turn (`sp`/`txt`/
    // `t`) mirrors so every consumer of encounter.consult.transcript renders it.
    if (Array.isArray(t.turns) && t.turns.length) {
      for (const turn of t.turns) {
        const text = String((turn && turn.text) || '').trim();
        if (!text) continue;
        const speaker = String((turn && turn.speaker) || 'Patient');
        const role = turnRole(speaker);
        const stamp = shortStamp(turn && turn.startMs);
        bubbles.push({ speaker, role, timestamp: stamp, elapsed, text, sp: speaker, txt: text, t: stamp });
      }
      elapsed += Math.max(1, Math.floor((t.durationMs || 8000) / 1000));
      continue;
    }
    if (!t.transcript) continue;
    const speaker = mediaField(m, 'speaker') || 'Patient';
    const role = mediaField(m, 'role') || 'patient';
    const stamp = fmtTime(mediaField(m, 'recordedAt'));
    bubbles.push({ speaker, role, timestamp: stamp, elapsed, text: t.transcript, sp: speaker, txt: t.transcript, t: stamp });
    elapsed += Math.max(1, Math.floor((t.durationMs || 8000) / 1000));
  }
  // REDESIGN §12 target home: the voice-intake verbatim rides as a
  // DocumentReference (type coding 'voice-transcript', attachment.data base64
  // text/plain). SACRED VERBATIM — decoded exactly as stored, never rephrased.
  for (const d of (Array.isArray(docRefs) ? docRefs : [])) {
    const code = (d.type && d.type.coding && d.type.coding[0] && d.type.coding[0].code) || '';
    if (code !== 'voice-transcript') continue;
    const att = (d.content && d.content[0] && d.content[0].attachment) || {};
    const text = att.data ? b64ToText(att.data) : '';
    if (!text) continue;
    bubbles.push({ speaker: 'Patient', role: 'patient', timestamp: fmtTime(d.date || (d.meta && d.meta.lastUpdated)), elapsed, text });
    elapsed += Math.max(1, Math.floor(text.length / 12));
  }
  return bubbles;
}

// ── header allergies / family history / chips (view-adapter.mjs:658-734) ────
export function shapeHeaderAllergies(intake, medKeys) {
  const a = (intake && intake.allergies) || {};
  const items = (Array.isArray(a.items) ? a.items.map((x) => x.name).filter(Boolean) : []).filter((name) => !textMentionsMed(name, medKeys));
  let status = a.status || 'unknown';
  if (!items.length && status === 'present') status = 'unknown';
  const rawText = a.text ? String(a.text).trim() : '';
  const raw = textMentionsMed(rawText, medKeys) ? '' : rawText;
  if (!items.length && status !== 'present' && raw && ABSENT_ALLERGY_RE.test(raw)) status = 'absent';
  let text = '';
  if (items.length) text = items.join(', ');
  else if (status === 'absent') text = raw && ABSENT_ALLERGY_RE.test(raw) ? raw : 'No known allergies';
  else if (raw && !/^none$/i.test(raw)) text = raw;
  return { status, text, items };
}
export function shapeHeaderFamilyHistory(intake, medKeys) {
  const all = Array.isArray(intake && intake.family_history && intake.family_history.items) ? intake.family_history.items : [];
  const items = all.map((f) => ({ relation: (f && f.relation) || '', condition: stripRelationFromCondition(f && f.relation, stripCrossedSentences(f && f.condition, medKeys)) })).filter((f) => f.condition);
  if (!items.length) return { text: '', items: [] };
  const text = items.slice(0, 3).map((f) => `${f.condition}${f.relation ? ` (${f.relation})` : ''}`).join(', ');
  return { text, items };
}
export function shapeContextChips(intake) {
  const chips = [];
  const cm = intake.current_medications.items[0];
  if (cm) chips.push(`${cm.name}${cm.dose ? ' ' + cm.dose : ''}${cm.frequency ? ' · ' + cm.frequency : ''}`.trim());
  if (intake.allergies.status === 'absent') chips.push('No known allergies');
  else if (intake.allergies.items.length) chips.push('Allergies: ' + intake.allergies.items.map((a) => a.name).join(', '));
  if (intake.family_history.items.length) {
    const fh = intake.family_history.items.slice(0, 2).map((f) => `${f.condition} (${f.relation})`).join(', ');
    chips.push('Family h/o · ' + fh);
  }
  // patientSnapshot.bmi/locality/language have no FHIR home in this graph fetch
  // → dropped (the muted locality/language tail chip is a nicety, not clinical).
  return chips;
}

// ── attachments (view-adapter.mjs:1850-1921) ────────────────────────────────
// DocumentReference (minus signed-prescription) + lab-order ServiceRequest +
// signed-prescription DocumentReference, all folded into one records strip.
// Coarse category is honoured from an OPD_SECTION_PAYLOAD-equivalent _healther
// bag when present, else inferred from the title/type. PRIOR-visit report
// folding (a second longitudinal query, view-adapter.mjs:1962-1988) is dropped
// here — this graph is THIS encounter only; the redesign can add a patient-wide
// DocumentReference search later without changing the screen.
// Faithful port of the server's canonical categorizer (server/opd/clinical-photos.mjs:63-85).
// CANONICAL ENUM (must match ARCH): xray | mri | ct | ecg | blood-report |
// lab-report | prescription | clinical-photo | lab-order | other. Always
// resolves to at least 'other' — never null (the records UI groups by this).
const RECORD_CATEGORIES = new Set([
  'xray', 'mri', 'ct', 'ecg', 'blood-report', 'lab-report',
  'prescription', 'clinical-photo', 'lab-order', 'other',
]);
function inferRecordCategory({ explicit, label, region, title, contentType } = {}) {
  const want = String(explicit || '').toLowerCase().trim();
  if (RECORD_CATEGORIES.has(want)) return want;
  const hay = `${label || ''} ${region || ''} ${title || ''}`.toLowerCase();
  // DISTINCT imaging leaves — keep MRI / CT / ECG out of the generic 'xray'
  // bucket so the records-UI tiles are truthful. Order matters: specific
  // modality keywords before the plain-radiograph fallback (same as server).
  if (/\bmri\b|magnetic resonance/.test(hay)) return 'mri';
  if (/\bct\b|cat scan|\bcect\b|\bncct\b|computed tomograph/.test(hay)) return 'ct';
  if (/\becg\b|\bekg\b|electrocardio|rhythm strip|12-?lead/.test(hay)) return 'ecg';
  if (/x-?ray|radiograph|ultrasound|\busg\b|sonograph|doppler|mammogram|\bscan\b/.test(hay)) return 'xray';
  if (/\bcbc\b|h(a|ae)mogram|blood (count|report|test|sugar)|\brbs\b|\bfbs\b|hba1c|lipid|\bcrp\b|\besr\b|\brft\b|\blft\b|thyroid|\btsh\b/.test(hay)) return 'blood-report';
  if (/prescription|\brx\b/.test(hay)) return 'prescription';
  if (/lab|urine|culture|biopsy|pathology|histo|report|investigation/.test(hay)) return 'lab-report';
  if (/photo|clinical/.test(hay) || String(contentType || '').startsWith('image/')) return 'clinical-photo';
  return 'other';
}
function shapeAttachments(docRefs, serviceReqs) {
  const attachments = (docRefs || [])
    // signed-prescription is folded in separately below; voice-transcript is the
    // consult chat verbatim (rendered as transcript bubbles), not a record card.
    .filter((r) => { const c = (r.type && r.type.coding && r.type.coding[0] && r.type.coding[0].code) || ''; return c !== 'signed-prescription' && c !== 'voice-transcript'; })
    .map((r) => {
      const a = (r.content && r.content[0] && r.content[0].attachment) || {};
      const code = (r.type && r.type.coding && r.type.coding[0] && r.type.coding[0].code) || 'document';
      const h = r._healther || {};
      const external = code === 'patient-upload' && h.origin === 'external-prior';
      const source = code === 'patient-upload' ? (external ? 'external' : 'patient') : 'this-visit';
      const sourceLabel = external ? 'From previous doctor' : source === 'patient' ? 'Patient-uploaded' : 'This hospital';
      const title = a.title || code;
      return {
        id: r.id, title, url: a.url || null, contentType: a.contentType || null,
        date: (r.meta && r.meta.lastUpdated) || r.date || null,
        source, sourceLabel,
        category: inferRecordCategory({ explicit: code === 'clinical-photo' ? 'clinical-photo' : (h.category || null), label: title, title, contentType: a.contentType }),
        summary: h.summary || null,
      };
    });
  for (const sr of (serviceReqs || [])) {
    if (!(sr._healther && sr._healther.kind === 'lab-order')) continue;
    const name = (sr.code && sr.code.text) || 'Lab order';
    attachments.push({ id: sr.id, title: name, url: null, contentType: null, date: sr.authoredOn || (sr.meta && sr.meta.lastUpdated) || null, source: 'this-visit', sourceLabel: 'This hospital', category: 'lab-order', summary: (sr.reasonCode && sr.reasonCode[0] && sr.reasonCode[0].text) || null });
  }
  for (const r of (docRefs || [])) {
    const code = (r.type && r.type.coding && r.type.coding[0] && r.type.coding[0].code) || '';
    if (code !== 'signed-prescription' && !(r._healther && r._healther.kind === 'signed-prescription')) continue;
    const a = (r.content && r.content[0] && r.content[0].attachment) || {};
    attachments.push({ id: r.id, title: a.title || 'Signed prescription', url: a.url || null, contentType: a.contentType || 'application/pdf', date: r.date || (r.meta && r.meta.lastUpdated) || null, source: 'this-visit', sourceLabel: 'This hospital', category: 'prescription', summary: null });
  }
  return attachments;
}

// getLatestVitals (vitals.mjs:102-121) — over the encounter's Observations.
// Pure-FHIR Observations carry no _healther.vital tag — they are disambiguated
// by their LOINC code (what the seed writes and what real devices emit); the
// legacy _healther.vital tag is kept as a fallback for mirrored disk rows.
const VITAL_LOINC = { bp: '85354-9', hr: '8867-4', spo2: '59408-5', temp: '8310-5', weight: '29463-7', height: '8302-2' };
function getLatestVitals(observations) {
  const obsList = (observations || [])
    .filter((o) => (o._healther && o._healther.kind === 'vital-sign') || (o.category || []).some((c) => (c.coding || []).some((x) => x.code === 'vital-signs')))
    .sort((a, b) => String(b.effectiveDateTime || '').localeCompare(String(a.effectiveDateTime || '')));
  const pick = (vital) => obsList.find((o) =>
    (o._healther && o._healther.vital === vital)
    || ((o.code && o.code.coding) || []).some((c) => c.code === VITAL_LOINC[vital]));
  const bp = pick('bp');
  const comp = (code) => { const c = (bp && bp.component || []).find((x) => (x.code && x.code.coding || []).some((y) => y.code === code)); return c && c.valueQuantity && c.valueQuantity.value; };
  const qv = (vital) => { const o = pick(vital); return o && o.valueQuantity ? o.valueQuantity.value : undefined; };
  const out = {
    bp: bp ? `${comp('8480-6') ?? '—'}/${comp('8462-4') ?? '—'}` : null,
    hr: qv('hr') ?? null, spo2: qv('spo2') ?? null, temp: qv('temp') ?? null,
    weight: qv('weight') ?? null, height: qv('height') ?? null,
    capturedAt: (obsList[0] && obsList[0].effectiveDateTime) || null,
  };
  const any = out.bp || out.hr != null || out.spo2 != null || out.temp != null || out.weight != null;
  return any ? out : null;
}

// listClinicalPhotos (clinical-photos.mjs:307-328) — clinical-photo DocumentReferences.
function listClinicalPhotos(docRefs) {
  return (docRefs || [])
    .filter((d) => (d.type && d.type.coding && d.type.coding[0] && d.type.coding[0].code === 'clinical-photo') || (d._healther && d._healther.kind === 'clinical-photo'))
    .map((d) => {
      const att = (d.content && d.content[0] && d.content[0].attachment) || {};
      const h = d._healther || {};
      const label = h.label || (h.region || 'Photo');
      return { id: d.id, region: h.region || 'other', label, reason: h.reason || null, url: att.url || null, contentType: att.contentType || null, source: h.source || 'kiosk', category: inferRecordCategory({ explicit: h.category, label, title: att.title, contentType: att.contentType }), date: (d.meta && d.meta.lastUpdated) || d.date || null };
    })
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

// dedupeMedRequests (state-machine.mjs:1185-1211) — keyed on name+dose.
function dedupeMedRequests(list) {
  const seen = new Set(); const out = [];
  for (const m of (list || [])) {
    if (m && m.status === 'entered-in-error') continue;
    const raw = (m && m.medicationCodeableConcept && m.medicationCodeableConcept.text) || (m && m.medicationCodeableConcept && m.medicationCodeableConcept.coding && m.medicationCodeableConcept.coding[0] && m.medicationCodeableConcept.coding[0].display) || '';
    const name = String(raw).trim().toLowerCase().replace(/\b\d+(?:\.\d+)?\s*(?:mg|mcg|ml|gm?|iu|units?|%)\b/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
    const dose = String((m && m.dosageInstruction && m.dosageInstruction[0] && m.dosageInstruction[0].text) || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const key = name ? `${name}|${dose}` : `__unnamed-${out.length}`;
    if (name && seen.has(key)) continue;
    if (name) seen.add(key);
    out.push(m);
  }
  return out;
}

// aiQuality (view-adapter.mjs:1221-1264). Section confidence bands come from the
// _payload/OPD_SECTION_PAYLOAD `confidence`/`status`; the verifier/clinicalSense
// (composition._healther.agenticLive) has no FHIR home in this fetch → null (the
// server itself returns null verifier/clinicalSense when agenticLive is absent).
function bandFor(confidence, flagged) {
  if (flagged) return 'low';
  if (typeof confidence !== 'number') return 'unknown';
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}
function shapeAiQuality(composition) {
  if (!(composition && composition.section)) return null;
  const sections = {};
  for (const s of composition.section) {
    const code = s.code && s.code.coding && s.code.coding[0] && s.code.coding[0].code;
    if (!code) continue;
    const p = sectionPayload(s);
    const confidence = typeof p.confidence === 'number' ? p.confidence : null;
    sections[code] = { confidence, status: p.status || null, band: bandFor(confidence, false), flagged: false };
  }
  return { sections, verifier: null, clinicalSense: null };
}

// ── the whole DTO (view-adapter.mjs:2081-2156) ──────────────────────────────
// `graph` = { encounter, patient, practitionerDisplay, questionnaireResponses,
//   compositions, medicationRequests, observations, documentReferences,
//   serviceRequests, media } — the fanned-in resource sets. medplum-encounter.js
//   assembles it from one search/GraphQL and calls this.
export function buildEncounterView(graph) {
  const enc = graph.encounter || {};
  const patient = graph.patient || null;
  const state = stateOf(enc);
  const patientId = (enc.subject && enc.subject.reference && enc.subject.reference.split('/')[1]) || (patient && patient.id) || null;

  // Freshest intake-summary Composition (view-adapter.mjs:1821-1828).
  const comps = graph.compositions || [];
  const byDate = (a, b) => String((b.date || (b.meta && b.meta.lastUpdated)) || '').localeCompare(String((a.date || (a.meta && a.meta.lastUpdated)) || ''));
  const intakeComps = comps.filter((c) => (c._healther && c._healther.kind === 'intake-summary') || (c.type && c.type.coding && c.type.coding[0] && c.type.coding[0].code === 'intake-summary')).sort(byDate);
  const composition = intakeComps[0];
  // Consult-draft Composition — the write-back target. Prefer the FHIR
  // 'consult-draft' type; also accept the legacy 'prescription' kind so a
  // seeded prescription Composition still lights up the draft.
  const draftComps = comps.filter((c) => (c.type && c.type.coding && c.type.coding[0] && (c.type.coding[0].code === CONSULT_DRAFT_CODE)) || (c._healther && (c._healther.kind === 'prescription' || c._healther.kind === 'consult-draft')) || (c.type && c.type.coding && c.type.coding[0] && c.type.coding[0].code === 'prescription')).sort(byDate);
  const rxComp = draftComps[0];
  // The patient-facing summary is a FINAL clinical artifact, not an Encounter
  // status inference and not the mutable consult draft. Require all three parts
  // of the sign contract: final status, visit-summary type, and the stable
  // encounter sign identifier. This prevents an unrelated final Composition or
  // a finished Encounter with a failed sign transaction from appearing signed.
  const signedComps = comps.filter((c) => {
    const typeCode = c.type?.coding?.[0]?.code || c.type?.text;
    const identifier = c.identifier || {};
    return c.status === 'final'
      && typeCode === VISIT_SUMMARY_CODE
      && identifier.system === SIGN_SYS
      && identifier.value === enc.id;
  }).sort(byDate);
  const signedComp = signedComps[0] || null;

  // Best answered QR (view-adapter.mjs:1810-1811).
  const qrs = graph.questionnaireResponses || [];
  const answeredSteps = (q) => ((q && q.item) || []).filter((it) => (it.item || []).some((s) => s.answer && s.answer.length)).length;
  const qr = qrs.reduce((best, cur) => (answeredSteps(cur) >= answeredSteps(best) ? cur : best), null);

  const meds = dedupeMedRequests(graph.medicationRequests || []);
  const media = graph.media || [];
  const docRefs = graph.documentReferences || [];
  const serviceReqs = graph.serviceRequests || [];
  const observations = graph.observations || [];

  const intake = shapeIntakeSections(composition, qr, enc);
  const verbatim = shapeIntakeVerbatim(qr);
  const liveFacts = shapeIntakeLiveFacts(qr);
  const draft = shapeConsultDraft(rxComp);
  const signedDraft = shapeConsultDraft(signedComp);
  const transcript = shapeConsultTranscript(media, docRefs);
  const chips = shapeContextChips(intake);
  const aiQuality = shapeAiQuality(composition);
  const medKeys = medNameKeysFromIntake(intake);
  const attachments = shapeAttachments(docRefs, serviceReqs);

  // Header displayName/age/sex/phone: from the REAL Patient resource (the
  // redesign source of truth), replacing the disk patientSnapshot the server
  // read. department/doctor come from the Encounter's own FHIR fields (the
  // appointmentSnapshot's FHIR equivalents).
  const displayName = patient ? nameOf(patient) : ((enc.subject && enc.subject.display) || 'Patient');
  const mrnId = patient && (patient.identifier || []).find((i) => i.system === MRN_SYS);
  const phoneT = patient && (patient.telecom || []).find((t) => t.system === 'phone');
  const department = (enc.serviceProvider && enc.serviceProvider.display) || (enc.serviceType && enc.serviceType[0] && enc.serviceType[0].text) || '';
  const doctor = graph.practitionerDisplay || (enc.participant && enc.participant[0] && enc.participant[0].individual && enc.participant[0].individual.display) || '';

  return {
    encounter: {
      id: enc.id,
      state: state,
      rawState: state,
      // An MRN (medical record number) is an issued patient identifier, never
      // something we can infer from an Encounter UUID. Keep it absent when the
      // Patient does not carry the canonical MRN identifier.
      mrn: (mrnId && mrnId.value) || null,
      openedAt: (enc.period && enc.period.start) || null,
      // No FHIR home for the payment flag in this fetch → screen-tolerated null.
      paid: null,
    },
    patient: {
      id: patientId,
      displayName,
      initials: initials(displayName),
      age: ageOf(patient && patient.birthDate) || (intake.personal_information && intake.personal_information.structured && intake.personal_information.structured.age) || null,
      sex: (patient && patient.gender) ? patient.gender[0].toUpperCase() : '',
      phone: (phoneT && phoneT.value) || '',
      // Language preference has no FHIR home in this fetch → default 'en'.
      language: 'en',
      department,
      doctor,
      allergies: shapeHeaderAllergies(intake, medKeys),
      familyHistory: shapeHeaderFamilyHistory(intake, medKeys),
      // priorContext.pastLine is dropped (no longitudinal query) → null.
      returningLine: null,
    },
    chips,
    intake,
    aiQuality,
    // priorContext: the second longitudinal query + countPriorVisits is dropped;
    // emit the SAME shape the server initialises before that best-effort block
    // (view-adapter.mjs:1946) so ConsultCompact's P('encounter.priorContext',…)
    // reads a real object, not the fallback.
    priorContext: { returning: false, visitCount: 0, lastVisit: null, lastDx: null, pastLine: null },
    // insights engines are LLM/longitudinal → the server's own catch-fallback
    // shapes (null / null / empty-arrays object). Screens tolerate these.
    insights: null,
    insightsRanked: null,
    insightsBar: { changes: [], trends: [], inconsistencies: [] },
    // markers/vitalSeries need a patient-wide Observation history + the
    // clinical-insights engine → dropped to [] (server drops to [] on failure).
    markers: [],
    vitalSeries: [],
    // Longitudinal episode care plan → null (empty shape when the patient has none).
    episode: null,
    // reviewConflicts runs post-draft via POST /consult/conflicts-review (bearer route).
    conflicts: [],
    attachments,
    vitals: getLatestVitals(observations),
    clinicalPhotos: listClinicalPhotos(docRefs),
    // vision biomarkers/triage are separate subsystems with no resources in this
    // fetch → the server's empty defaults.
    biomarkers: [],
    triage: null,
    verbatim,
    liveFacts,
    intakeAudio: media.filter((m) => mediaField(m, 'sessionType') === 'opd-intake').map((m) => { const t = mediaTranscript(m); return { sessionId: mediaField(m, 'sessionId'), durationSec: Math.max(0, Math.floor((t.durationMs || 0) / 1000)), transcript: t.transcript || '', url: mediaField(m, 'audioUrl') || null }; }),
    consult: {
      transcript,
      // Writer.jsx reads encounter.consult.draft.* and needs the STRUCTURED
      // med shape (freq array / duration object) — see toWriterDraft above.
      draft: toWriterDraft(draft),
      signed: !!signedComp,
    },
    // Immutable post-sign truth. Portal boot explicitly binds its cards to this
    // snapshot and these signature facts; staff draft editing continues to use
    // the preliminary Composition above.
    signedDraft,
    visitSummary: signedComp ? {
      compositionId: signedComp.id,
      status: signedComp.status,
      signedAt: signedComp.date || signedComp.meta?.lastUpdated || null,
      signerRef: signedComp.author?.[0]?.reference || null,
      signerDisplay: signedComp.author?.[0]?.display || null,
    } : null,
    // ConsultCompact.jsx:1379 reads P('encounter.draft', {}) and needs the
    // STRING med shape (frequency '1-0-1') its editor + draftBody() round-trip
    // — without this top-level key its editable state initialises blank
    // despite a fully-populated draft Composition.
    draft,
    medications: meds.map((m) => ({
      name: (m.medicationCodeableConcept && m.medicationCodeableConcept.text) || 'Medication',
      dose: (m.dosageInstruction && m.dosageInstruction[0] && m.dosageInstruction[0].text) || '',
      reason: (m.reasonCode && m.reasonCode[0] && m.reasonCode[0].text) || '',
    })),
    // WRITE-BACK POINTERS (redesign): the consult-draft Composition the write
    // agent PATCHes/signs with If-Match. Not read by any screen pick-path — a
    // side-channel for medplum-encounter.js to surface to the write layer.
    // `null` id means "no draft Composition exists yet — create one on first save".
    draftRef: rxComp ? { id: rxComp.id, versionId: (rxComp.meta && rxComp.meta.versionId) || null, code: CONSULT_DRAFT_CODE } : { id: null, versionId: null, code: CONSULT_DRAFT_CODE },
  };
}
